// Right-click on an RF sensor tile: every setting of that sensor in one menu.
//
// The device cards have had their ⋮ menu for a long time; an RF sensor tile
// only opened the settings dialog on a click. This is the sensor's equivalent,
// opened at the mouse with a right-click (the tile's `onMenu`):
//
//   Settings…              the full RfSensorDialog — name, type, location/room,
//                          codes, notifications, who is told, replace hardware
//   Details & history…     RF number, hw id, receiver, owner, codes, event log
//   Automations…           the sensor's automations
//   Reminders…             nag ONE named person while it stays open (door /
//                          safe / shutter only — a state must be able to end)
//   Mute for me / Unmute   this user only (RfDeviceMute)
//   Send test notification same audience, wording and sound as a real event
//   Who can see it…        per-user ACCESS (manage only)
//   Unassign…              back to a fresh sensor (manage only)
//
// ⚠️ THE MENU IS NOT THE GATE. "Who can see it" and "Unassign" are shown only
// to a Super Admin, the location's owner or the sensor's MasterUserId, AND the
// backend checks the same three (rfDevices.canManageRf) — hiding an item is not
// a permission (CLAUDE.md).
//
// ⚠️ ACCESS IS A REMOVAL LIST (the NoAccess flag on RfDeviceMute — no table of its own). Nobody listed = everyone who
// can see the room, as before. A removed person does not see the sensor and is
// never notified by it, not even by a rule naming them. The location's owner
// can never be removed.
//
// ⚠️ UNASSIGN SHOWS THE COUNT BEFORE THE WRITE (a DryRun first), the way
// "Remove room" does: switch actions and automations are DELETED, not hidden.

import React, { useEffect, useState } from 'react';
import {
  Menu, MenuItem, ListItemIcon, ListItemText, Divider, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Typography, Box, Alert, Switch,
  LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, Chip, Snackbar,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import SnoozeIcon from '@mui/icons-material/Snooze';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CampaignIcon from '@mui/icons-material/Campaign';
import GroupIcon from '@mui/icons-material/Group';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useApp } from '../../core/state/AppContext';
import {
  getRfDeviceAudience, setRfDeviceMute, testRfNotify, getRfEventLog,
  getRfDeviceAccess, setRfDeviceAccess,
} from '../../core/api/api';
import { rfNumber } from '../../core/devices/rfIdentify';
import { rfType } from '../../core/devices/rfTypes';
import { rfAgo } from './RfSensorTile';
import { nameOf } from './RfSensorDialog';
import AutomationsDialog from './AutomationsDialog';
import RfRemindersDialog from './RfRemindersDialog';
import { canRemind } from '../../core/reminders/reminderRules';
import { FIT_SM, FIT_MD } from './fitDialog';
import RfUnassignDialog, { canManageRfSensor } from './RfUnassignDialog';

export { canManageRfSensor };

// A 4xx from these endpoints carries { error } — say it, never "failed".
const errText = (e, fallback) => e?.response?.data?.error || e?.message || fallback;

// ── Details & history ─────────────────────────────────────────────────────
function DetailsDialog({ device, onClose }) {
  const [log, setLog] = useState(null);
  useEffect(() => {
    if (!device) return undefined;
    let alive = true;
    setLog(null);
    getRfEventLog({ RfDeviceId: device.RfDeviceId, Days: 30, Limit: 50 })
      .then((r) => { if (alive) setLog(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setLog([]); });
    return () => { alive = false; };
  }, [device]);
  if (!device) return null;
  const t = rfType(device.RfType);
  const rows = [
    ['RF number', rfNumber(device.RfDeviceId)],
    ['Type', `${t.icon} ${t.label}`],
    ['Location', device.LocationName || '—'],
    ['Room', Number(device.RoomID) > 0 ? (device.RoomName || `Room ${device.RoomID}`) : 'Whole location'],
    ['Owner', device.OwnerName || '—'],
    ['Hardware id', device.TransmitterId],
    ['Heard by receiver', device.ReceiverUniqueNumber || '—'],
    ['Notifications', Number(device.Notify) ? 'On' : 'Off'],
    ['Last event', device.LastAt ? `${device.LastEvent || 'heard'} · ${rfAgo(device.LastAt)}` : 'Nothing heard yet'],
  ];
  return (
    <Dialog open onClose={onClose} {...FIT_MD}>
      <DialogTitle>{nameOf(device)}</DialogTitle>
      <DialogContent dividers>
        <Table size="small">
          <TableBody>
            {rows.map(([k, v]) => (
              <TableRow key={k}>
                <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap', width: 1 }}>{k}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{v}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Codes</Typography>
        {(device.Codes || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">No codes learned.</Typography>
        ) : (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {device.Codes.map((c) => (
              <Chip
                key={c.REMOTEVALUE}
                size="small"
                label={`${c.DeviceAction || 'unnamed'} · ${c.REMOTEVALUE}`}
                variant={c.DeviceAction ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Last 30 days</Typography>
        {log == null && <LinearProgress />}
        {log && log.length === 0 && (
          <Typography variant="body2" color="text.secondary">No events in the last 30 days.</Typography>
        )}
        {log && log.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Event</TableCell>
                <TableCell align="right">Actions</TableCell>
                <TableCell align="right">Told</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {log.map((e) => (
                <TableRow key={e.RfEventId}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{rfAgo(e.At)}</TableCell>
                  <TableCell>{e.Meaning || `#${e.RemoteValue}`}</TableCell>
                  <TableCell align="right">{e.ActionsFired ?? 0}</TableCell>
                  <TableCell align="right">{e.Notified ?? 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ── Who can see it ────────────────────────────────────────────────────────
function AccessDialog({ device, onClose, onChanged }) {
  const { userId } = useApp();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(0);        // the UserId being saved
  const [error, setError] = useState('');

  const load = () => getRfDeviceAccess(device.RfDeviceId)
    .then((r) => setData(r.data))
    .catch((e) => { setData({ users: [], ready: false }); setError(errText(e, 'Could not load the list.')); });
  useEffect(() => { if (device) { setError(''); setData(null); load(); } }, [device]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!device) return null;
  const toggle = async (u) => {
    setBusy(u.UserId);
    setError('');
    try {
      await setRfDeviceAccess(device.RfDeviceId, u.UserId, !Number(u.HasAccess), userId);
      await load();
      onChanged && onChanged();
    } catch (e) {
      setError(errText(e, 'Not saved.'));
    } finally {
      setBusy(0);
    }
  };

  return (
    <Dialog open onClose={onClose} {...FIT_SM}>
      <DialogTitle>Who can see “{nameOf(device)}”</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Everyone who can see the room sees this sensor. Switch a person off to hide it
          from them. They will not see it and will get none of its notifications.
        </Alert>
        {data && data.ready === false && !error && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            The server is not set up for this yet (rf_access_migration.sql has not been run).
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {data == null && <LinearProgress />}
        {data && data.users.length === 0 && (
          <Typography variant="body2" color="text.secondary">Nobody else has this location.</Typography>
        )}
        {data && data.users.map((u) => (
          <Box key={u.UserId} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            <Box sx={{ width: 0, flex: '1 1 0', minWidth: 180 }}>
              <Typography sx={{ fontWeight: 600 }} noWrap>
                {u.FullName || `User ${u.UserId}`}
                {Number(u.UserId) === Number(userId) ? ' (you)' : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {u.MobileNumber || ''}{Number(u.IsOwner) ? ' · owner — always has access' : ''}
              </Typography>
            </Box>
            <Switch
              checked={!!Number(u.HasAccess)}
              disabled={!data.ready || !!Number(u.IsOwner) || busy === u.UserId}
              onChange={() => toggle(u)}
              inputProps={{ 'aria-label': `Access for ${u.FullName || u.UserId}` }}
            />
          </Box>
        ))}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Done</Button></DialogActions>
    </Dialog>
  );
}

/**
 * @param target      { device, x, y } from the tile's onMenu, or null
 * @param onClose     close the menu
 * @param onSettings  (device) open the full RfSensorDialog — the host owns it
 * @param onChanged   reload the host's sensors (after access / unassign)
 */
export default function RfSensorMenu({ target, onClose, onSettings, onChanged }) {
  const app = useApp();
  const { userId } = app;
  const device = target?.device || null;
  const manage = canManageRfSensor(device, app);

  const [muted, setMuted] = useState(null);   // null = not known yet
  const [toast, setToast] = useState('');
  const [details, setDetails] = useState(null);
  const [access, setAccess] = useState(null);
  const [unassign, setUnassign] = useState(null);
  const [automations, setAutomations] = useState(null);
  const [reminders, setReminders] = useState(null);

  // Whether *I* muted it — asked each time the menu opens, so it is never stale.
  useEffect(() => {
    if (!device) return undefined;
    let alive = true;
    setMuted(null);
    getRfDeviceAudience(device.RfDeviceId)
      .then((r) => { if (alive) setMuted((r.data?.muted || []).map(Number).includes(Number(userId))); })
      .catch(() => { if (alive) setMuted(null); });
    return () => { alive = false; };
  }, [device, userId]);

  const pick = (fn) => () => { const d = device; onClose(); fn(d); };

  const toggleMute = pick(async (d) => {
    try {
      await setRfDeviceMute(d.RfDeviceId, userId, !muted);
      setToast(muted ? `Notifications on for “${nameOf(d)}”` : `“${nameOf(d)}” muted for you`);
    } catch (e) {
      setToast(errText(e, 'Not saved.'));
    }
  });

  const test = pick(async (d) => {
    const r = await testRfNotify(d.RfDeviceId, 'Test').catch((e) => ({ error: e }));
    // { ok:false, error:'nobody would be told' } comes back as a 200.
    if (r.error) setToast(errText(r.error, 'Test not sent.'));
    else if (r.data && r.data.ok === false) setToast(`Test not sent: ${r.data.error || 'nobody would be told'}`);
    else {
      const n = Number(r.data?.sent || 0);
      setToast(n > 0 ? `Test sent (${n} delivered)` : 'Test sent, but nobody could be reached (no app token or Telegram)');
    }
  });

  return (
    <>
      <Menu
        open={!!target}
        onClose={onClose}
        anchorReference="anchorPosition"
        anchorPosition={target ? { top: target.y, left: target.x } : undefined}
        onContextMenu={(e) => { e.preventDefault(); onClose(); }}
      >
        <MenuItem disabled sx={{ opacity: '0.75 !important' }}>
          <ListItemText
            primary={device ? nameOf(device) : ''}
            secondary={device ? rfNumber(device.RfDeviceId) : ''}
            primaryTypographyProps={{ fontWeight: 700, variant: 'body2' }}
          />
        </MenuItem>
        <Divider />
        <MenuItem onClick={pick(onSettings)}>
          <ListItemIcon><TuneIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Settings…" secondary="Name, room, codes, notifications, who is told" />
        </MenuItem>
        <MenuItem onClick={pick(setDetails)}>
          <ListItemIcon><InfoOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Details &amp; history…</ListItemText>
        </MenuItem>
        <MenuItem onClick={pick(setAutomations)}>
          <ListItemIcon><AutoModeIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Automations…</ListItemText>
        </MenuItem>
        {canRemind(device) && (
          <MenuItem onClick={pick(setReminders)}>
            <ListItemIcon><SnoozeIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Reminders…" secondary="Nag someone while it stays open" />
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={toggleMute} disabled={muted == null}>
          <ListItemIcon>
            {muted ? <NotificationsActiveIcon fontSize="small" /> : <NotificationsOffIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{muted ? 'Unmute for me' : 'Mute for me'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={test}>
          <ListItemIcon><CampaignIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Send test notification</ListItemText>
        </MenuItem>
        {manage && <Divider />}
        {manage && (
          <MenuItem onClick={pick(setAccess)}>
            <ListItemIcon><GroupIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Who can see it…" secondary="Give or remove a person's access" />
          </MenuItem>
        )}
        {manage && (
          <MenuItem onClick={pick(setUnassign)} sx={{ color: 'error.main' }}>
            <ListItemIcon><LinkOffIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Unassign…</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <DetailsDialog device={details} onClose={() => setDetails(null)} />
      <AccessDialog device={access} onClose={() => setAccess(null)} onChanged={onChanged} />
      <RfUnassignDialog
        device={unassign}
        onClose={() => setUnassign(null)}
        onDone={() => { setUnassign(null); onChanged && onChanged(); }}
      />
      {reminders && (
        <RfRemindersDialog
          open
          onClose={() => setReminders(null)}
          device={reminders}
        />
      )}
      {automations && (
        <AutomationsDialog
          open
          onClose={() => setAutomations(null)}
          locationId={Number(automations.LocationID) || 0}
          locationName={automations.LocationName || ''}
          forSensor={{ rfDeviceId: automations.RfDeviceId }}
        />
      )}
      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
